// Typed settings catalogue (architecture sprint-11 §5.1). Shared by the server (validation, resolver, DTOs)
// and the client (labels, controls). Dependency-free (shared-purity.test.ts): metadata and pure helpers only.
//
// The catalogue is EXHAUSTIVE for sprint 11: any other key is a 404 on every settings endpoint. Keys with
// `available: false` are reserved for a later sprint: not listed, not writable through the API, but a
// template may carry them (supervisor.viewAttendance on Shift operations, Decision 17).
//
// Sprint 12 (architecture sprint-12 §5.1): five new keys (Manage members, member notes) and the retired
// `project.membershipMode` (every project is members-only). A retired key keeps its type so old stored rows
// still type-check, but it is unavailable (404 on every settings endpoint) and never copied from a template.
//
// Sprint 15 (architecture sprint-15 §3.3, §5.1): the fixed notes rule. supervisor.addNotes, supervisor.readNotes
// and notes.authorsReadOwn are retired; notes.enabled alone gates every note capability, readOwnNotes is gone.

//
// Sprint 13 (architecture sprint-13 §5.1): the `shifts` group (shift mode, work location, auto-close, roster
// zone, attendance graces), two new Supervisor keys, supervisor.viewAttendance becomes available, and the
// `timezone` kind (an IANA zone, or null = use DEFAULT_TIMEZONE).
//
// Sprint 14 (architecture sprint-14 §5.1): the `alarms` group (check-in alarms on shift projects), the
// `localTime` kind ('HH:MM' or null), supervisor.checkinAlerts and shift.inactivityCloseMinutes. The alarm keys
// are inert unless shift mode is on: alarmsActive() derives that, there is no new resolver source (B-8).
//
// Sprint 19 (architecture sprint-19 §4): the roster is gone. supervisor.manageRoster, attendance.lateGraceMinutes, attendance.earlyEndGraceMinutes and shift.autoCloseGraceMinutes are removed from the catalogue (0010 deletes their stored rows); roster.zone is relabelled "Shift time zone".

export type SettingLevel = 'global' | 'project' | 'user';
export type SettingGroup = 'membership' | 'supervisors' | 'notes' | 'shifts' | 'alarms' | 'tracking';
export type SettingModeValue = 'default' | 'enforced';
export type MembershipMode = 'open' | 'assigned';
export type CheckinInterval = 15 | 30 | 45 | 60;
export type WorkLocationMode = 'off' | 'optional' | 'required';
export type HardMaxHours = 8 | 10 | 12 | 14 | 16 | 24;
export type AlarmIntervalMinutes = 15 | 20 | 30 | 45 | 60;
export type AlarmConfirmMinutes = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
export type AlarmSound = 'klaxon' | 'bell' | 'siren';
export type InactivityCloseMinutes = 30 | 60 | 90 | 120 | 180 | 240;

export interface ChoiceOption {
  value: string | number;
  label: string;
}

export type ValueKind =
  | { type: 'boolean' }
  | { type: 'choice'; options: readonly ChoiceOption[] }
  /** ISO 1..7, 1–7 unique items, stored normalised ascending. */
  | { type: 'weekdays' }
  /** Sprint 13: an IANA zone name, or null (= the server's DEFAULT_TIMEZONE). */
  | { type: 'timezone' }
  /** Sprint 14: a wall-clock 'HH:MM' (00:00–23:59), or null (empty). */
  | { type: 'localTime' };

/** The value type of every key, so resolver results are typed per key. */
export interface SettingValueMap {
  'project.membershipMode': MembershipMode;
  'supervisor.changeSettings': boolean;
  'supervisor.viewTeam': boolean;
  'supervisor.exportTeam': boolean;
  'supervisor.viewAttendance': boolean;
  'supervisor.manageMembers': boolean;
  'supervisor.addNotes': boolean;
  'supervisor.readNotes': boolean;
  'notes.enabled': boolean;
  'notes.authorsReadOwn': boolean;
  'supervisor.correctShifts': boolean;
  'supervisor.checkinAlerts': boolean;
  'shift.enabled': boolean;
  'shift.workLocation': WorkLocationMode;
  'shift.hardMaxHours': HardMaxHours;
  'shift.inactivityCloseMinutes': InactivityCloseMinutes;
  'roster.zone': string | null;
  'alarm.enabled': boolean;
  'alarm.intervalMinutes': AlarmIntervalMinutes;
  'alarm.windowStart': string | null;
  'alarm.windowEnd': string | null;
  'alarm.zone': string | null;
  'alarm.confirmMinutes': AlarmConfirmMinutes;
  'alarm.sound': AlarmSound;
  'workweek.days': number[];
  'checkin.enabled': boolean;
  'checkin.intervalMinutes': CheckinInterval;
  'entries.manualAllowed': boolean;
  'entries.selfEditAllowed': boolean;
  'timer.pauseAllowed': boolean;
  'timer.switchAllowed': boolean;
}

export type SettingKey = keyof SettingValueMap;

export interface SettingDef<K extends SettingKey = SettingKey> {
  key: K;
  label: string;
  description: string;
  group: SettingGroup;
  kind: ValueKind;
  levels: readonly SettingLevel[];
  default: SettingValueMap[K];
  /** A Supervisor with supervisor.changeSettings may change the VALUE. */
  delegable: boolean;
  /** Never delegable, even with delegation on. */
  adminOnly: boolean;
  writeVia: 'settings' | 'membership-mode-endpoint';
  /** false = reserved for a later sprint (or retired): not listed, not writable via the API. */
  available: boolean;
  /** Sprint 12: no longer used. Stored values are ignored and templates never copy it. */
  retired?: boolean;
  sprint: number;
}

export const SETTING_KEY_PATTERN = /^[a-z][A-Za-z0-9]*(\.[a-z][A-Za-z0-9]*)+$/;
export const SETTING_KEY_MAX_LENGTH = 64;

const ISO_DAYS = 7;
const BOOLEAN: ValueKind = { type: 'boolean' };
const CHECKIN_INTERVAL_OPTIONS: readonly ChoiceOption[] = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
];
const MEMBERSHIP_OPTIONS: readonly ChoiceOption[] = [
  { value: 'open', label: 'Open to all users' },
  { value: 'assigned', label: 'Assigned members only' },
];
const WORK_LOCATION_OPTIONS: readonly ChoiceOption[] = [
  { value: 'off', label: 'Off' },
  { value: 'optional', label: 'Optional' },
  { value: 'required', label: 'Required' },
];
const HARD_MAX_OPTIONS: readonly ChoiceOption[] = [8, 10, 12, 14, 16, 24].map((n) => ({ value: n, label: `${n} h` }));
const minutesOptions = (values: readonly number[]): readonly ChoiceOption[] => values.map((n) => ({ value: n, label: `${n} min` }));
export const ALARM_INTERVAL_VALUES = [15, 20, 30, 45, 60] as const;
export const ALARM_CONFIRM_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
export const INACTIVITY_CLOSE_VALUES = [30, 60, 90, 120, 180, 240] as const;
export const ALARM_SOUND_VALUES = ['klaxon', 'bell', 'siren'] as const;
const ALARM_SOUND_OPTIONS: readonly ChoiceOption[] = [
  { value: 'klaxon', label: 'Klaxon' },
  { value: 'bell', label: 'Bell' },
  { value: 'siren', label: 'Siren' },
];
const LOCAL_TIME: ValueKind = { type: 'localTime' };
/** 'HH:MM', 00:00–23:59. */
export const LOCAL_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

const P: readonly SettingLevel[] = ['project'];
const GP: readonly SettingLevel[] = ['global', 'project'];
const GPU: readonly SettingLevel[] = ['global', 'project', 'user'];
const PU: readonly SettingLevel[] = ['project', 'user'];

/** Catalogue order is the display order inside each group. */
export const SETTINGS_CATALOGUE: { readonly [K in SettingKey]: SettingDef<K> } = {
  'project.membershipMode': {
    key: 'project.membershipMode',
    label: 'Membership',
    description: 'Who can log time on this project.',
    group: 'membership',
    kind: { type: 'choice', options: MEMBERSHIP_OPTIONS },
    levels: P,
    default: 'open',
    delegable: false,
    adminOnly: true,
    writeVia: 'membership-mode-endpoint',
    // Retired in sprint 12: every project is members-only.
    available: false,
    retired: true,
    sprint: 11,
  },
  'supervisor.changeSettings': {
    key: 'supervisor.changeSettings',
    label: 'Supervisors can change project settings',
    description: "Lets this project's Supervisors change the time tracking settings below.",
    group: 'supervisors',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    available: true,
    sprint: 11,
  },
  'supervisor.viewTeam': {
    key: 'supervisor.viewTeam',
    label: 'Supervisors can view team records and reports',
    description: "Records, Missing entries and the Team report for this project's time only.",
    group: 'supervisors',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    available: true,
    sprint: 11,
  },
  'supervisor.exportTeam': {
    key: 'supervisor.exportTeam',
    label: 'Supervisors can export team data',
    description: "PDF, CSV and XLSX downloads of this project's team data.",
    group: 'supervisors',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    available: true,
    sprint: 11,
  },
  'supervisor.viewAttendance': {
    key: 'supervisor.viewAttendance',
    label: 'Supervisors can view attendance',
    description: 'Supervisors see who is on shift and the attendance of this project.',
    group: 'supervisors',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    available: true,
    sprint: 13,
  },
  'supervisor.correctShifts': {
    key: 'supervisor.correctShifts',
    label: 'Supervisors can correct shifts',
    description: 'Supervisors can correct, add and void shifts. Needs View attendance.',
    group: 'supervisors',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    available: true,
    sprint: 13,
  },
  'supervisor.checkinAlerts': {
    key: 'supervisor.checkinAlerts',
    label: 'Supervisors get missed check-in alerts',
    description: 'Supervisors see a live badge and get a browser notification for missed check-ins. Needs View attendance.',
    group: 'supervisors',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    available: true,
    sprint: 14,
  },
  'supervisor.manageMembers': {
    key: 'supervisor.manageMembers',
    label: 'Supervisors can manage members',
    description: "Supervisors can add, invite, change and remove members of this project. They can't change Admins or themselves.",
    group: 'supervisors',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    available: true,
    sprint: 12,
  },
  'supervisor.addNotes': {
    key: 'supervisor.addNotes',
    label: 'Supervisors can add member notes',
    description: "Supervisors can add notes about members. They can't edit or delete them.",
    group: 'supervisors',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    // Retired in sprint 15: the fixed notes rule replaces it (stored rows are kept for a rollback and ignored).
    available: false,
    retired: true,
    sprint: 12,
  },
  'supervisor.readNotes': {
    key: 'supervisor.readNotes',
    label: 'Supervisors can read all member notes',
    description: 'Supervisors can read every note on this project.',
    group: 'supervisors',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    // Retired in sprint 15: the fixed notes rule replaces it (stored rows are kept for a rollback and ignored).
    available: false,
    retired: true,
    sprint: 12,
  },
  'notes.enabled': {
    key: 'notes.enabled',
    label: 'Member notes',
    description: 'Turns member notes on for this project.',
    group: 'notes',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 12,
  },
  'notes.authorsReadOwn': {
    key: 'notes.authorsReadOwn',
    label: 'Authors can read their own notes',
    description: 'Supervisors who add notes can read the notes they wrote.',
    group: 'notes',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    // Retired in sprint 15: the fixed notes rule replaces it (stored rows are kept for a rollback and ignored).
    available: false,
    retired: true,
    sprint: 12,
  },
  'shift.enabled': {
    key: 'shift.enabled',
    label: 'Shift mode',
    description: "Members get Start Shift and End Shift instead of the timer and manual entries. Can't be turned off while someone is on shift.",
    group: 'shifts',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: false,
    adminOnly: true,
    writeVia: 'settings',
    available: true,
    sprint: 13,
  },
  'shift.workLocation': {
    key: 'shift.workLocation',
    label: 'Work location',
    description: "Ask for On site or Remote when a shift starts. It's self-declared.",
    group: 'shifts',
    kind: { type: 'choice', options: WORK_LOCATION_OPTIONS },
    levels: P,
    default: 'off',
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 13,
  },
  'shift.hardMaxHours': {
    key: 'shift.hardMaxHours',
    label: 'Longest shift',
    description: 'A forgotten shift closes automatically after this long.',
    group: 'shifts',
    kind: { type: 'choice', options: HARD_MAX_OPTIONS },
    levels: P,
    default: 12,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 13,
  },
  'shift.inactivityCloseMinutes': {
    key: 'shift.inactivityCloseMinutes',
    label: 'Auto-close after no activity',
    description: 'On alarm projects, a shift closes automatically this long after the last confirmation or heartbeat.',
    group: 'shifts',
    kind: { type: 'choice', options: minutesOptions(INACTIVITY_CLOSE_VALUES) },
    levels: P,
    default: 90,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 14,
  },
  'roster.zone': {
    key: 'roster.zone',
    label: 'Shift time zone',
    description: 'Check-in alarm times use this zone unless an alarm time zone is set.',
    group: 'shifts',
    kind: { type: 'timezone' },
    levels: P,
    default: null,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 13,
  },
  'alarm.enabled': {
    key: 'alarm.enabled',
    label: 'Check-in alarms',
    description: 'A loud alarm rings in the Clockit tab at fixed times during a shift. People confirm with one tap.',
    group: 'alarms',
    kind: BOOLEAN,
    levels: P,
    default: false,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 14,
  },
  'alarm.intervalMinutes': {
    key: 'alarm.intervalMinutes',
    label: 'Alarm every',
    description: 'Alarms ring on the clock: every 30 min means :00 and :30.',
    group: 'alarms',
    kind: { type: 'choice', options: minutesOptions(ALARM_INTERVAL_VALUES) },
    levels: P,
    default: 30,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 14,
  },
  'alarm.windowStart': {
    key: 'alarm.windowStart',
    label: 'Alarms from',
    description: 'Alarms ring only between these times, which may cross midnight. Leave both empty for the whole shift.',
    group: 'alarms',
    kind: LOCAL_TIME,
    levels: P,
    default: null,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 14,
  },
  'alarm.windowEnd': {
    key: 'alarm.windowEnd',
    label: 'Alarms until',
    description: 'Alarms ring only between these times, which may cross midnight. Leave both empty for the whole shift.',
    group: 'alarms',
    kind: LOCAL_TIME,
    levels: P,
    default: null,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 14,
  },
  'alarm.zone': {
    key: 'alarm.zone',
    label: 'Alarm time zone',
    description: 'The zone of the alarm times. Empty means the shift time zone.',
    group: 'alarms',
    kind: { type: 'timezone' },
    levels: P,
    default: null,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 14,
  },
  'alarm.confirmMinutes': {
    key: 'alarm.confirmMinutes',
    label: 'Confirm within',
    description: "An alarm that isn't confirmed within this time counts as missed.",
    group: 'alarms',
    kind: { type: 'choice', options: minutesOptions(ALARM_CONFIRM_VALUES) },
    levels: P,
    default: 3,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 14,
  },
  'alarm.sound': {
    key: 'alarm.sound',
    label: 'Alarm sound',
    description: 'Members can pick their own sound unless this is enforced.',
    group: 'alarms',
    kind: { type: 'choice', options: ALARM_SOUND_OPTIONS },
    levels: PU,
    default: 'klaxon',
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 14,
  },
  'workweek.days': {
    key: 'workweek.days',
    label: 'Working days',
    description: 'Drives Missing entries.',
    group: 'tracking',
    kind: { type: 'weekdays' },
    levels: GP,
    default: [1, 2, 3, 4, 5],
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 11,
  },
  'checkin.enabled': {
    key: 'checkin.enabled',
    label: '"Still working?" prompt',
    description: 'Asks whether you are still working while a timer runs.',
    group: 'tracking',
    kind: BOOLEAN,
    levels: GPU,
    default: true,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 11,
  },
  'checkin.intervalMinutes': {
    key: 'checkin.intervalMinutes',
    label: 'Prompt interval',
    description: 'How often the "Still working?" prompt appears.',
    group: 'tracking',
    kind: { type: 'choice', options: CHECKIN_INTERVAL_OPTIONS },
    levels: GPU,
    default: 30,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 11,
  },
  'entries.manualAllowed': {
    key: 'entries.manualAllowed',
    label: 'Manual entries',
    description: 'Lets people add time entries by hand (Log time).',
    group: 'tracking',
    kind: BOOLEAN,
    levels: GP,
    default: true,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 11,
  },
  'entries.selfEditAllowed': {
    key: 'entries.selfEditAllowed',
    label: 'Edit and delete own entries',
    description: 'Lets people edit and delete their own saved entries.',
    group: 'tracking',
    kind: BOOLEAN,
    levels: GP,
    default: true,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 11,
  },
  'timer.pauseAllowed': {
    key: 'timer.pauseAllowed',
    label: 'Pause the timer',
    description: 'Shows Pause on a running timer.',
    group: 'tracking',
    kind: BOOLEAN,
    levels: GP,
    default: true,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 11,
  },
  'timer.switchAllowed': {
    key: 'timer.switchAllowed',
    label: '"I switched"',
    description: 'Lets people switch a running timer to another activity.',
    group: 'tracking',
    kind: BOOLEAN,
    levels: GP,
    default: true,
    delegable: true,
    adminOnly: false,
    writeVia: 'settings',
    available: true,
    sprint: 11,
  },
};

export const SETTING_KEYS = Object.keys(SETTINGS_CATALOGUE) as SettingKey[];
/** Display order. The Membership group no longer renders (sprint 12: its only key is retired). */
export const SETTING_GROUP_ORDER: readonly SettingGroup[] = ['supervisors', 'notes', 'shifts', 'alarms', 'tracking'];
export const SETTING_GROUP_LABELS: { readonly [G in SettingGroup]: string } = {
  membership: 'Membership',
  supervisors: 'Supervisors',
  notes: 'Member notes',
  shifts: 'Shifts',
  alarms: 'Check-in alarms',
  tracking: 'Time tracking',
};

/**
 * Sprint 13 (§5.2): keys a shift-mode project forces off (source 'shift-mode'), so the existing `rules` DTOs
 * and `editable` flags carry shift mode to the client with no new client logic.
 */
export const SHIFT_FORCED_OFF_KEYS = [
  'entries.manualAllowed',
  'entries.selfEditAllowed',
  'timer.pauseAllowed',
  'timer.switchAllowed',
  'checkin.enabled',
] as const satisfies readonly SettingKey[];

/** Sprint 13 (§4.2): writes to these keys answer 503 until the 0006 schema exists. */
export const SHIFT_SCHEMA_KEYS = [
  'shift.enabled',
  'shift.workLocation',
  'shift.hardMaxHours',
  'roster.zone',
  'supervisor.correctShifts',
  'supervisor.viewAttendance',
] as const satisfies readonly SettingKey[];

/** Sprint 14 (§4.2): writes to these keys (and the alarmSound preference) answer 503 until the 0007 schema exists. */
export const ALARM_SCHEMA_KEYS = [
  'alarm.enabled',
  'alarm.intervalMinutes',
  'alarm.windowStart',
  'alarm.windowEnd',
  'alarm.zone',
  'alarm.confirmMinutes',
  'alarm.sound',
  'supervisor.checkinAlerts',
  'shift.inactivityCloseMinutes',
] as const satisfies readonly SettingKey[];

/** Keys that are no longer used: never copied from a template into a new project (sprint 12, A-4). */
export const RETIRED_KEYS: readonly SettingKey[] = SETTING_KEYS.filter((k) => SETTINGS_CATALOGUE[k].retired === true);

/** Never project-scoped, ever (§5.1). Used by the catalogue test. */
export const NEVER_PROJECT_SCOPED_NAMES = ['theme', 'shortcuts', 'auth', 'session', 'invite', 'timezone', 'defaultProject', 'notification'] as const;

/**
 * Project capabilities checked by requireProjectCapability (sprint 12 §5.2). The four direct ones are each
 * backed by one supervisor.* key; the note capabilities are derived at resolve time (notes.enabled gates them)
 * and `editNotes` is held only by Admins.
 */
export type DirectProjectCapability = 'viewTeam' | 'exportTeam' | 'changeSettings' | 'manageMembers';
/** Sprint 13 adds two DERIVED capabilities (shift mode gates them): viewAttendance, correctShifts. */
export type ProjectCapability =
  | DirectProjectCapability
  | 'addNotes'
  | 'readNotes'
  | 'exportNotes'
  | 'editNotes'
  | 'viewAttendance'
  | 'correctShifts'
  /** Sprint 14 (B-14): the live missed check-in badge, the alerts feed and (non-Admins) browser notifications. */
  | 'checkinAlerts';
export const PROJECT_CAPABILITIES: readonly ProjectCapability[] = [
  'viewTeam',
  'exportTeam',
  'changeSettings',
  'manageMembers',
  'addNotes',
  'readNotes',
  'exportNotes',
  'editNotes',
  'viewAttendance',
  'correctShifts',
  'checkinAlerts',
];
export type ProjectCaps = { [C in ProjectCapability]: boolean };
export const CAPABILITY_KEYS: { readonly [C in DirectProjectCapability]: SettingKey } = {
  viewTeam: 'supervisor.viewTeam',
  exportTeam: 'supervisor.exportTeam',
  changeSettings: 'supervisor.changeSettings',
  manageMembers: 'supervisor.manageMembers',
};

/** The resolved supervisor.* / notes.* values that §5.2's formulas read. */
export interface CapabilityInputs {
  membersOnly: boolean;
  viewTeam: boolean;
  exportTeam: boolean;
  changeSettings: boolean;
  manageMembers: boolean;
  notesEnabled: boolean;
  /** Sprint 13: the 0006 schema exists AND shift.enabled resolves true for the project. */
  shiftOn: boolean;
  viewAttendance: boolean;
  correctShifts: boolean;
  /** Sprint 14: the 0007 schema exists. */
  alarmReady: boolean;
  /** Sprint 14: supervisor.checkinAlerts resolved for the project. */
  checkinAlerts: boolean;
}

/** §5.2 (sprint 12) and sprint 13 §5.4: an explicit Supervisor's capabilities. Pure, so the client and a unit test share it. */
export function deriveSupervisorCaps(i: CapabilityInputs): ProjectCaps {
  // Sprint 15 (§5.1): the fixed notes rule. notes.enabled alone gates the note capabilities; export no longer
  // needs exportTeam (owner decision 2); editNotes stays Admin-only (author edits go through readNotes + §5.3).
  const notesOn = i.membersOnly && i.notesEnabled;
  const viewAttendance = i.shiftOn && i.viewAttendance;
  return {
    viewTeam: i.viewTeam,
    exportTeam: i.exportTeam,
    changeSettings: i.changeSettings,
    manageMembers: i.membersOnly && i.manageMembers,
    addNotes: notesOn,
    readNotes: notesOn,
    exportNotes: notesOn,
    editNotes: false,
    viewAttendance,
    // A-10: corrections are made from the Attendance tab, so they need viewAttendance too.
    correctShifts: viewAttendance && i.correctShifts,
    // Sprint 14 (B-14): shiftOn && alarmReady && supervisor.checkinAlerts && supervisor.viewAttendance.
    checkinAlerts: viewAttendance && i.alarmReady && i.checkinAlerts,
  };
}

/** An Admin holds every capability. */
export function allProjectCaps(): ProjectCaps {
  return Object.fromEntries(PROJECT_CAPABILITIES.map((c) => [c, true])) as ProjectCaps;
}

export function isSettingKey(key: string): key is SettingKey {
  return Object.prototype.hasOwnProperty.call(SETTINGS_CATALOGUE, key);
}

export function settingDef<K extends SettingKey>(key: K): SettingDef<K> {
  return SETTINGS_CATALOGUE[key] as SettingDef<K>;
}

export function hasLevel(def: SettingDef, level: SettingLevel): boolean {
  return def.levels.includes(level);
}

/** `enforced` is only meaningful for keys a user can also set (level U). */
export function canBeEnforced(def: SettingDef): boolean {
  return hasLevel(def, 'user');
}

/** Available keys with the given level, in catalogue order. */
export function availableKeysAt(level: SettingLevel): SettingKey[] {
  return SETTING_KEYS.filter((k) => SETTINGS_CATALOGUE[k].available && hasLevel(SETTINGS_CATALOGUE[k], level));
}

export type NormalisedValue = { ok: true; value: unknown } | { ok: false };

function isWeekdayList(value: unknown): value is number[] {
  if (!Array.isArray(value) || value.length < 1 || value.length > ISO_DAYS) return false;
  if (!value.every((d) => typeof d === 'number' && Number.isInteger(d) && d >= 1 && d <= ISO_DAYS)) return false;
  return new Set(value).size === value.length;
}

/** Validates a value against the key's kind; weekdays come back sorted ascending. Never throws. */
export function normaliseSettingValue(def: SettingDef, value: unknown): NormalisedValue {
  switch (def.kind.type) {
    case 'boolean':
      return typeof value === 'boolean' ? { ok: true, value } : { ok: false };
    case 'choice':
      return def.kind.options.some((o) => o.value === value) ? { ok: true, value } : { ok: false };
    case 'weekdays':
      return isWeekdayList(value) ? { ok: true, value: [...value].sort((a, b) => a - b) } : { ok: false };
    case 'timezone':
      return value === null || isIanaZone(value) ? { ok: true, value } : { ok: false };
    case 'localTime':
      return value === null || (typeof value === 'string' && LOCAL_TIME_PATTERN.test(value)) ? { ok: true, value } : { ok: false };
  }
}

const MAX_ZONE_LENGTH = 64;

/** Valid when Intl accepts it (a global, so the catalogue stays import-free). */
function isIanaZone(value: unknown): value is string {
  if (typeof value !== 'string' || value.trim() === '' || value.length > MAX_ZONE_LENGTH || value !== value.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** Deep equality for the value kinds above (booleans, choice scalars, weekday arrays). */
export function sameSettingValue(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((v, i) => v === b[i]);
  return a === b;
}
